import React from "react";
import { Box, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { BROWN, NEUTRAL, ORANGE, RADIUS, SELECTED_EDGE, VIOLET } from "../../styles/brandTokens";
import type { TheoOverview } from "../../types/integration";
import type { OverviewSourceKind } from "./OverviewSourceDialog";
import { cardSx, formatCount, text } from "../monitor/SummaryPanel";
import {
  BarSegment,
  assistedTechnicians,
  canListPeople,
  assistedView,
  stateSegments,
  timeSavedHeadline,
  timeSavedSegments,
} from "./theoOverviewModel";

const plural = (count: number, one: string, many: string) => `${formatCount(count)} ${count === 1 ? one : many}`;
const headlineSx = { ...text(24, NEUTRAL.text, 600), lineHeight: 1.25 };

/** Proportional bar. Decorative: the legend beside it carries the same numbers as text. */
export const SegmentBar: React.FC<{ segments: BarSegment[]; height?: number }> = ({ segments, height = 10 }) => {
  const total = segments.reduce((sum, segment) => sum + segment.count, 0);
  if (total <= 0) return null;
  return (
    <Box
      aria-hidden="true"
      sx={{ display: "flex", height, borderRadius: `${height / 2}px`, overflow: "hidden", background: NEUTRAL.hover, gap: "2px" }}
    >
      {segments
        .filter((segment) => segment.count > 0)
        .map((segment) => (
          <Box
            key={segment.key}
            title={`${segment.valueLabel ?? formatCount(segment.count)} ${segment.label}`}
            sx={{ minWidth: 6, width: `${(segment.count / total) * 100}%`, background: segment.color }}
          />
        ))}
    </Box>
  );
};

export const SegmentLegend: React.FC<{ segments: BarSegment[]; stacked?: boolean }> = ({ segments, stacked = false }) => (
  <Box
    component="ul"
    sx={{
      listStyle: "none",
      m: 0,
      p: 0,
      mt: 1.25,
      display: "flex",
      flexDirection: stacked ? "column" : "row",
      flexWrap: "wrap",
      columnGap: 2,
      rowGap: stacked ? 0.75 : 0.5,
    }}
  >
    {segments
      .filter((segment) => segment.primary || segment.count > 0)
      .map((segment) => (
        <Box component="li" key={segment.key} sx={{ display: "inline-flex", alignItems: "baseline", gap: 0.75 }}>
          <Box
            component="span"
            aria-hidden="true"
            sx={{ width: 8, height: 8, borderRadius: "50%", background: segment.color, flexShrink: 0, alignSelf: "center" }}
          />
          <Box
            component="span"
            sx={{ ...text(13.5, segment.primary ? NEUTRAL.text : NEUTRAL.textMuted, 600), fontVariantNumeric: "tabular-nums" }}
          >
            {segment.valueLabel ?? formatCount(segment.count)}
          </Box>
          <Box component="span" sx={text(13.5, segment.primary ? NEUTRAL.textSecondary : NEUTRAL.textMuted)}>
            {segment.label}
            {segment.detail ? (
              <Box component="span" sx={{ color: NEUTRAL.textMuted }}>{` · ${segment.detail}`}</Box>
            ) : null}
          </Box>
        </Box>
      ))}
  </Box>
);

const Eyebrow: React.FC<{ color: string; large?: boolean; children: React.ReactNode }> = ({ color, large = false, children }) => (
  <Typography
    component="p"
    sx={{
      ...text(large ? 14 : 11.5, NEUTRAL.textSecondary, 700),
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      mb: 0.75,
      display: "flex",
      alignItems: "center",
      gap: 0.75,
    }}
  >
    <Box component="span" aria-hidden="true" sx={{ width: large ? 8 : 6, height: large ? 8 : 6, borderRadius: "50%", background: color }} />
    {children}
  </Typography>
);

const AgentTickets: React.FC<{ overview: TheoOverview; agentName: string | null; onShowSource: (kind: OverviewSourceKind) => void }> = ({
  overview,
  agentName,
  onShowSource,
}) => {
  const total = overview.agent_tickets.total;
  const segments = stateSegments(overview);
  const emptyText = agentName
    ? `${agentName} hasn't worked any tickets in this window`
    : "No agent has worked a ticket in this window";
  return (
    <Box component="section" aria-labelledby="overview-agents">
      <Eyebrow color={VIOLET.main} large>
        Autonomous: Agents
      </Eyebrow>
      <Typography id="overview-agents" component="h2" sx={{ ...headlineSx, mb: total > 0 ? 1.75 : 0 }}>
        {total === 0 ? (
          emptyText
        ) : (
          <>
            {agentName || "Agents"} worked{" "}
            <SourceLink color={VIOLET.theoText} title="See the tickets" onClick={() => onShowSource("agent-tickets")}>
              {plural(total, "ticket", "tickets")}
            </SourceLink>{" "}
            on {agentName ? "its" : "their"} own
          </>
        )}
      </Typography>
      {total > 0 ? (
        <>
          <SegmentBar segments={segments} />
          <SegmentLegend segments={segments} />
        </>
      ) : null}
    </Box>
  );
};

const AssistedStat: React.FC<{ value: number; label: string; onOpen?: () => void }> = ({ value, label, onOpen }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography sx={{ ...text(26, BROWN.main, 600), lineHeight: 1.1, fontVariantNumeric: "tabular-nums" }}>
      {onOpen && value > 0 ? (
        <SourceLink color={BROWN.main} title={`See the ${label.split(",")[0]}`} onClick={onOpen}>
          {formatCount(value)}
        </SourceLink>
      ) : (
        formatCount(value)
      )}
    </Typography>
    <Typography sx={{ ...text(13.5, NEUTRAL.textSecondary), mt: 0.5 }}>{label}</Typography>
  </Box>
);

/** Reads as part of the sentence; the dotted underline and hover say it opens the records behind it. */
const sourceLinkSx = (color: string) => ({
  border: 0,
  background: "transparent",
  p: 0,
  font: "inherit",
  letterSpacing: "inherit",
  lineHeight: "inherit",
  color,
  cursor: "pointer",
  textDecoration: "underline dotted",
  textDecorationThickness: "2px",
  textUnderlineOffset: "5px",
  borderRadius: `${RADIUS.control}px`,
  "&:hover": { textDecorationStyle: "solid" },
  "&:focus-visible": { outline: "none", boxShadow: SELECTED_EDGE },
});

const SourceLink: React.FC<{ color: string; title: string; onClick: () => void; children: React.ReactNode }> = ({
  color,
  title,
  onClick,
  children,
}) => (
  <Box component="button" type="button" onClick={onClick} aria-haspopup="dialog" title={title} sx={sourceLinkSx(color)}>
    {children}
  </Box>
);

const AssistedWork: React.FC<{
  overview: TheoOverview;
  onShowTechnicians: () => void;
  onShowSource: (kind: OverviewSourceKind) => void;
}> = ({ overview, onShowTechnicians, onShowSource }) => {
  const view = assistedView(overview);
  // The lists name technicians, so they are admin-only like the technicians list.
  const listable = canListPeople(overview);
  if (view.kind === "hidden") return null;
  return (
    <Box
      component="section"
      aria-labelledby="overview-assisted"
      sx={{ borderTop: `1px solid ${NEUTRAL.border}`, pt: 2.25, mt: 2.5 }}
    >
      <Eyebrow color={BROWN.main} large>
        AI Assisted: Technician
      </Eyebrow>
      {view.kind === "error" ? (
        <Typography id="overview-assisted" role="alert" sx={text(13.5, ORANGE.text)}>
          Couldn't load technicians' work with Theo.
        </Typography>
      ) : (
        <>
          <Typography id="overview-assisted" component="h2" sx={{ ...headlineSx, mb: 1.75 }}>
            {view.technicians === 0 ? (
              "No technician worked with Theo in this window"
            ) : (
              <>
                {/* The list is admin-only (like Usage Insights); others see the count as plain text. */}
                {assistedTechnicians(overview).length > 0 ? (
                  <SourceLink color={BROWN.main} title="See who" onClick={onShowTechnicians}>
                    {plural(view.technicians, "technician", "technicians")}
                  </SourceLink>
                ) : (
                  <Box component="span" sx={{ color: BROWN.main }}>
                    {plural(view.technicians, "technician", "technicians")}
                  </Box>
                )}{" "}
                worked with Theo
              </>
            )}
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 3 }}>
            <AssistedStat
              value={view.tickets}
              label="tickets, prompting Theo in ticket chat"
              onOpen={listable ? () => onShowSource("assisted-tickets") : undefined}
            />
            <AssistedStat
              value={view.conversations}
              label="Chat Assistant conversations, outside a ticket"
              onOpen={listable ? () => onShowSource("conversations") : undefined}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

const explainButtonSx = {
  border: 0,
  background: "transparent",
  p: 0,
  mt: 2,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 0.5,
  ...text(13, VIOLET.main, 600),
  borderRadius: `${RADIUS.control}px`,
  "&:hover": { color: VIOLET.dark, textDecoration: "underline" },
  "&:focus-visible": { outline: "none", boxShadow: SELECTED_EDGE },
};

const TimeSaved: React.FC<{ overview: TheoOverview; onExplain: () => void; onShowSource: (kind: OverviewSourceKind) => void }> = ({
  overview,
  onExplain,
  onShowSource,
}) => {
  const actions = overview.actions;
  const headline = timeSavedHeadline(actions?.estimated_minutes || 0);
  const segments = timeSavedSegments(overview);
  return (
    <Box component="section" aria-labelledby="overview-time-saved" sx={{ minWidth: 0, width: "100%" }}>
      <Eyebrow color={VIOLET.main}>Time saved</Eyebrow>
      {actions ? (
        <>
          <Typography id="overview-time-saved" component="h2" sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
            <Box
              component="span"
              sx={{ ...text(44, VIOLET.theoText, 700), lineHeight: 1, letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums" }}
            >
              {actions.total > 0 ? (
                <SourceLink color={VIOLET.theoText} title="See the actions" onClick={() => onShowSource("time-saved")}>
                  {headline.value}
                </SourceLink>
              ) : (
                headline.value
              )}
            </Box>
            <Box component="span" sx={text(18, NEUTRAL.text, 600)}>
              {headline.unit}
            </Box>
          </Typography>
          <Typography sx={{ ...text(13.5, NEUTRAL.textSecondary), mt: 0.75 }}>
            {headline.days ? `About ${headline.days} of hands-on work, from ` : "From "}
            {plural(actions.total, "action", "actions")} Theo completed
          </Typography>
          {segments.length > 1 && actions.estimated_minutes > 0 ? (
            <Box sx={{ mt: 2 }}>
              <SegmentBar segments={segments} height={6} />
              <SegmentLegend segments={segments} stacked />
            </Box>
          ) : null}
        </>
      ) : (
        <Typography id="overview-time-saved" role="alert" sx={text(13.5, ORANGE.text)}>
          Couldn't load Theo's actions.
        </Typography>
      )}
      <Box component="button" type="button" onClick={onExplain} sx={explainButtonSx}>
        <InfoOutlinedIcon sx={{ fontSize: 16 }} aria-hidden="true" />
        How we estimate this
      </Box>
    </Box>
  );
};

interface OverviewHeroProps {
  overview: TheoOverview;
  agentName: string | null;
  onExplainTimeSaved: () => void;
  onShowTechnicians: () => void;
  /** Opens the records behind a headline figure. */
  onShowSource: (kind: OverviewSourceKind) => void;
}

const OverviewHero: React.FC<OverviewHeroProps> = ({ overview, agentName, onExplainTimeSaved, onShowTechnicians, onShowSource }) => (
  <Box
    sx={{
      ...cardSx,
      p: { xs: 2, md: 2.5 },
      display: "grid",
      gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(0, 3fr) minmax(0, 2fr)" },
      gap: { xs: 2, md: 3 },
    }}
  >
    <Box sx={{ minWidth: 0 }}>
      <AgentTickets overview={overview} agentName={agentName} onShowSource={onShowSource} />
      <AssistedWork overview={overview} onShowTechnicians={onShowTechnicians} onShowSource={onShowSource} />
    </Box>
    <Box
      sx={{
        borderTop: { xs: `1px solid ${NEUTRAL.border}`, md: 0 },
        borderLeft: { xs: 0, md: `1px solid ${NEUTRAL.border}` },
        pt: { xs: 2, md: 0 },
        pl: { xs: 0, md: 3 },
        display: "flex",
        alignItems: "flex-start",
      }}
    >
      <TimeSaved overview={overview} onExplain={onExplainTimeSaved} onShowSource={onShowSource} />
    </Box>
  </Box>
);

export default OverviewHero;
